import { afterNextRender, Component, computed, DestroyRef, inject, signal, ViewChild } from '@angular/core';
import { NavigationEnd, NavigationSkipped, Router, RouterOutlet } from '@angular/router';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs/operators';

// MDB Modules
import { MdbSidenavComponent, MdbSidenavModule } from 'mdb-angular-ui-kit/sidenav';
import { MdbCollapseModule } from 'mdb-angular-ui-kit/collapse';
import { MdbDropdownModule } from 'mdb-angular-ui-kit/dropdown';

// Components
import { Footer } from './shared/components/footer/footer';
import { Gradientsegmentbar } from './shared/components/gradientsegmentbar/gradientsegmentbar';

// Services
import { AuthService } from './core/services/auth';
import { UserService } from './core/services/user';
import { RouterLink } from '@angular/router';

@Component({
  selector: 'app-root',
  imports: [
    RouterOutlet,
    CommonModule,
    Footer,
    MdbSidenavModule,
    MdbCollapseModule,
    MdbDropdownModule,
    Gradientsegmentbar, RouterLink
  ],
  templateUrl: './app.html',
  styleUrl: './app.scss'
})
export class App {
  @ViewChild('sidenav') sidenav!: MdbSidenavComponent;

  private router = inject(Router);
  private authService = inject(AuthService);
  private userService = inject(UserService);
  private destroyRef = inject(DestroyRef);

  isLoginPage = signal<boolean>(false);
  isAuthenticated = computed(() => this.authService.isAuthenticated());
  userName = signal<string>('');

  screenWidth = signal(typeof window !== 'undefined' ? window.innerWidth : 1024);
  //on mobile screens the menu is set to 'over'
  sidenavMode = computed(() => {
    return this.screenWidth() <= 1079 ? 'over' : 'side';
  });

  isSidenavOpen = signal<boolean>(false);
  // Listen for window resize - change the sidenav menu behavior on smaller screens
  private resizeHandler = () => {
    this.screenWidth.set(window.innerWidth);
  };

  constructor() {
    this.checkLoginPage(this.router.url);

    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      takeUntilDestroyed()
    ).subscribe((event: NavigationEnd) => {
      this.checkLoginPage(event.urlAfterRedirects);
      //reload user info after navigation
      this.loadUserInfo();
    });
    //Close Side Nav if mobile
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd || event instanceof NavigationSkipped),
      takeUntilDestroyed()
    ).subscribe(() => {
      this.closeSidenavOnOverlay();
    });

    if (typeof window !== 'undefined') {
      window.addEventListener('resize', this.resizeHandler);
    }
  }

  private loadUserInfo() {
    const userInfo = this.userService.getUserInfoFromStorage();
    if (userInfo) {
      this.userName.set(`${userInfo.firstName} ${userInfo.lastName}`);
    } else {
      this.userName.set('');
    }
  }

  private checkLoginPage(currentUrl: string) {
    const currentPath = currentUrl.split(/[?#]/, 1)[0].replace(/\/+$/, '') || '/';
    this.isLoginPage.set(currentPath === '/' || currentPath === '/login');
  }

  onLogoutClick() {
    if (this.sidenav?.isOpen) {
      this.sidenav.hide();
    }
    this.isSidenavOpen.set(false);
    this.authService.logout().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.userService.clearUserInfo();
      this.userName.set('');
      this.router.navigate(['/login']);
    });
  }

  //#region Side Nav
  shouldShowSidenav(): boolean {
    return !this.isLoginPage();
  }

  toggleSidenav() {
    if (this.isSidenavOpen()) {
      this.isSidenavOpen.set(false);
      this.sidenav.hide();
    } else {
      this.isSidenavOpen.set(true);
      this.sidenav.show();
    }
  }

  closeSidenav() {
    this.isSidenavOpen.set(false);
    this.sidenav.hide();
  }

  setSidenavOpen(isOpen: boolean) {
    this.isSidenavOpen.set(isOpen);
  }

  // On Mobile Sidenav overlays over the screen - change the behavior to hide the menu 
  // after navigation
  // In 'side' mode it's docked alongside the page, so leave it as the user set it.
  private closeSidenavOnOverlay(): void {
    if (this.sidenavMode() !== 'over') {
      return;
    }

    this.isSidenavOpen.set(false);
    this.sidenav?.hide();
  }
  //#endregion
}
